<template>
  <div class="active-plan">
    <BusinessPlan v-if="isBusinessSubscription" :plan="subscription" />
    <template v-else>
      <PlanDetails
        v-bind="{
          planName: plan && plan.name ? plan.name : 'Legacy Plan',
          accountCredit,
          isBusinessSubscription,
          isPlanActive,
          isPlanFailed,
          isPlanInCancelledState,
          isPlanPending,
          planExpiryDate,
          daysRemainingForSub,
        }"
      />
      <PlanActions
        v-if="canViewPlanActions"
        v-bind="{
          isLoading,
          isPlanActive,
          isPlanFailed,
          isPlanInCancelledState,
          isPremiumPlan,
          planExpiryDate,
        }"
        @cancel-renewal-clicked="handleCancelRenewalClick"
        @reactivate-renewal-clicked="handleReactivateRenewalClick"
        @upgrade-plan-clicked="handleUpgradeClick"
        @update-payment-method="$emit('is-managing-payment')"
      />
    </template>
  </div>
</template>

<script>
import {
  formatSubDate,
  getDaysRemainingForSub,
  isBusinessPlan,
} from '@/helpers'
import CONSTANTS from '@/constants'
import BusinessPlan from './active-plan/BusinessPlan.vue'
import PlanDetails from './active-plan/PlanDetails.vue'
import PlanActions from './active-plan/PlanActions.vue'

export default {
  name: 'ActivePlan',
  components: {
    BusinessPlan,
    PlanDetails,
    PlanActions,
  },
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    accountCredit: {
      type: String,
      required: true,
    },
    newPlanCodes: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    annualPlanCode: {
      type: String,
      required: true,
    },
  },
  computed: {
    isBusinessSubscription() {
      const { subscription } = this
      return isBusinessPlan(subscription)
    },
    plan() {
      const {
        subscription: { plan },
      } = this
      return plan
    },
    isLegacyPlan() {
      const { subscription } = this
      return (
        subscription.plan_code &&
        !this.newPlanCodes.includes(subscription.plan_code)
      )
    },
    isPremiumPlan() {
      const {
        subscription: { plan_code },
      } = this
      return CONSTANTS.PLANS.PREMIUM_PLANS.includes(plan_code)
    },
    isPlanInCancelledState() {
      const {
        subscription: { state },
      } = this
      return state === CONSTANTS.SUBSCRIPTIONS.CANCELLED_STATE
    },
    isPlanActive() {
      const {
        subscription: { state },
      } = this
      return state === CONSTANTS.SUBSCRIPTIONS.ACTIVE_STATE
    },
    isPlanFailed() {
      const {
        subscription: { state },
      } = this
      return state === CONSTANTS.SUBSCRIPTIONS.FAILED_STATE
    },
    isPlanPending() {
      const {
        subscription: { state },
      } = this
      return state === CONSTANTS.SUBSCRIPTIONS.PENDING_STATE
    },
    canViewPlanActions() {
      return (
        (this.isPlanActive ||
          this.isPlanFailed ||
          this.isPlanInCancelledState) &&
        !this.isBusinessSubscription
      )
    },
    planExpiryDate() {
      const {
        subscription: { expires_at },
      } = this
      return formatSubDate(expires_at)
    },
    daysRemainingForSub() {
      const { subscription } = this
      return getDaysRemainingForSub(subscription)
    },
  },
  methods: {
    handleCancelRenewalClick() {
      const { subscription } = this
      this.$emit('cancel-subscription-renewal', subscription)
    },
    handleReactivateRenewalClick() {
      const {
        subscription: { id },
      } = this
      this.$emit('reactivate-subscription-renewal', { id })
    },
    handleUpgradeClick() {
      const {
        subscription: { id, plan_code },
      } = this
      if (plan_code && plan_code.length && plan_code === this.annualPlanCode) {
        this.$emit('upgrade-plan-to-premium', id)
      } else {
        this.$emit('upgrade-plan', id)
      }
    },
  },
}
</script>

<style lang="scss">
.active-plan {
  display: flex;
  flex-direction: column;
  margin: 0 0 25px 0;
  flex-wrap: wrap;
  background: var(--glass-mid-gray);
  border-radius: 5px;
}
</style>
